import React, { useState, useEffect, useMemo } from "react";
import { Country } from "country-state-city";
import { api, apiJson } from "../../../app/Api/ApiSlice";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

// --- Plan Configuration ---
const plans = [
    {
        id: 1,
        name: "National",
        price: "1200",
        features: ["Sell nationally", "Basic Support", "1 Country Only", "Add multiple admins"],
        countryLimit: 1,
    },
    {
        id: 2,
        name: "International",
        price: "1800",
        features: ["Priority Support", "Up to 10 Countries", "Add multiple admins"],
        countryLimit: 10,
    },
    {
        id: 3,
        name: "Global",
        price: "2800",
        features: ["24/7 Support", "All Countries Included"],
        countryLimit: Infinity,
    },
];

const PlanSelect = () => {
    const [selectedPlanId, setSelectedPlanId] = useState(plans[0].id);
    const [selectedCountries, setSelectedCountries] = useState([]); // [{ value, label }]
    const [allCountries, setAllCountries] = useState([]); // [{ value, label }]
    const [isLoading, setIsLoading] = useState(false)
    const [storePlan, setStorePlan] = useState("")
    const [contacts, setContacts] = useState({})

    const { name } = useParams()
    const navigate = useNavigate()

    useEffect(() => {
        const getStorePlan = async () => {
            try {
                const response = await api.get(`/stores/${name}/manage/plan`)
                // console.log(response)
                setStorePlan(response.data.storeSubPlan)
                setContacts({contacts: response.data.contacts,_id: response.data._id});
            } catch (error) {

            }
        }
        getStorePlan()
    }, [])

    // Auto-detect user country via IP and store as {value, label}
    useEffect(() => {
        const getUserCountry = async () => {
            try {
                const res = await fetch("https://ipapi.co/json/");
                const data = await res.json();
                if (data && data.country_code && data.country_name) {
                    setSelectedCountries([{ value: data.country_code, label: data.country_name }]);
                }
            } catch (error) {
                console.log("Country detection failed", error);
            }
        };

        getUserCountry();
    }, []);

    // Load countries list from country-state-city and map to { value, label }
    useEffect(() => {
        try {
            const countries = Country.getAllCountries().map((c) => ({
                value: c.isoCode,
                label: c.name,
            }));
            setAllCountries(countries);
        } catch (err) {
            console.error("Failed to load country list", err);
            setAllCountries([]);
        }
    }, []);

    const currentPlan = useMemo(() => plans.find((p) => p.id === selectedPlanId) || plans[0], [selectedPlanId]);
    const countryLimit = currentPlan.countryLimit;

    const handleSelectPlan = (planId) => {
        const plan = plans.find((p) => p.id === planId);
        setSelectedPlanId(planId);

        if (!plan) return;

        if (plan.countryLimit === Infinity) {
            // Global plan => clear manual selection (all included)
            setSelectedCountries([]);
        } else if (selectedCountries.length > plan.countryLimit) {
            // Trim to new limit
            setSelectedCountries((prev) => prev.slice(0, plan.countryLimit));
        }
    };

    const handleCountryToggle = (country) => {
        if (!country) return;

        const already = selectedCountries.some((c) => c.value === country.value);

        if (already) {
            setSelectedCountries((prev) => prev.filter((c) => c.value !== country.value));
        } else {
            if (selectedCountries.length >= countryLimit) {
                alert(`You can only select up to ${countryLimit} countries for the ${currentPlan.name} plan.`);
                return;
            }
            setSelectedCountries((prev) => [...prev, country]);
        }
    };


    // console.log(currentPlan)

    const handleContinue = async () => {
        const details = {
            plan: currentPlan.name,
            price: currentPlan.price,
            countries: countryLimit === Infinity ? "All Countries" : selectedCountries.map((c) => c.label),
            storeContacts: contacts,
        };

        if (currentPlan.name === "National") {

            try {
                setIsLoading(true)
                const response = await api.post(`/stores/${name}/manage/plan`, details)
                setIsLoading(false)
                toast.success(response.data.message)
                navigate(`/b/manage/${name}/plan/checkout`, { state: { details } })
            } catch (error) {
                setIsLoading(false)
                toast.error(error.response.data.message)
            }
        }

        else if (storePlan === "International" && currentPlan.name === "National" || storePlan === "International" && currentPlan.name === "Global") {
            try {
                setIsLoading(true)
                const response = await api.post(`/${name}/manage/plan`, details)
                setIsLoading(false)
                toast.success(response.data.message)
                navigate(`/b/manage/${name}/plan/checkout`, { state: { details } })
            } catch (error) {
                toast.error(error.response.data.message)
            }
        }
        else {
            navigate(`/b/manage/${name}/plan/checkout`, { state: { details } })
        }
    };

    const CountrySelector = () => {
        if (countryLimit === Infinity) {
            return <p className="country-info country-info--all">All Countries are included with the {currentPlan.name} plan.</p>;
        }

        const isLimitReached = selectedCountries.length >= countryLimit;

        return (
            <div className="country-selector-section">
                <h3 className="selector-title">Select Supported Countries ({selectedCountries.length} / {countryLimit})</h3>

                <select
                    className="country-select-dropdown"
                    value=""
                    onChange={(e) => {
                        const code = e.target.value;
                        if (!code) return;
                        const obj = allCountries.find((c) => c.value === code);
                        if (obj) handleCountryToggle(obj);
                    }}
                    disabled={isLimitReached}
                >
                    <option disabled value="">{isLimitReached ? `Limit of ${countryLimit} Reached` : "Select a Country to Add"}</option>

                    {allCountries
                        .filter((c) => !selectedCountries.some((sc) => sc.value === c.value))
                        .map((country) => (
                            <option key={country.value} value={country.value}>
                                {country.label}
                            </option>
                        ))}
                </select>

                <div className="selected-countries-tags">
                    {selectedCountries.map((country) => (
                        <span key={country.value} className="country-tag">
                            {country.label}
                            <button type="button" className="remove-country-btn" onClick={() => handleCountryToggle(country)}>
                                &times;
                            </button>
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    const continueDisabled = countryLimit !== Infinity && selectedCountries.length === 0;

    return (
        <div className="plan-container">
            <h2 className="plan-title">Choose Your Plan</h2>
            <hr className="divider" />

            <div className="plan-list">
                {plans.map((plan) => (
                    <div
                        key={plan.id}
                        className={`plan-card ${selectedPlanId === plan.id ? "plan-card--selected" : ""}`}
                        onClick={() => handleSelectPlan(plan.id)}
                    >
                        <h3>{plan.name}</h3>
                        <p className="plan-price">Ksh {plan.price} / Month</p>
                        <p className="plan-country-info">
                            {plan.countryLimit === Infinity ? "All Countries" : `${plan.countryLimit} Countries Included`}
                        </p>

                        <ul>
                            {plan.features.map((f, i) => (
                                <li key={i}>{f}</li>
                            ))}
                        </ul>

                        <button type="button" className={`select-btn ${selectedPlanId === plan.id ? "select-btn--selected" : ""}`}>
                            {selectedPlanId === plan.id ? "Selected" : "Select"}
                        </button>
                    </div>
                ))}
            </div>
            <CountrySelector />
            <button
                className={`continue-btn ${!continueDisabled ? "continue-btn--active" : ""}`}
                onClick={handleContinue}
                disabled={continueDisabled}
            >
                {isLoading ? "please wait ..." : "continue"}
            </button>
        </div>
    );
};

export default PlanSelect;